import type { AppState } from './default-state';
import { createDefaultState } from './default-state';
import { reduce } from './reducer';
import { advanceTime } from './ticker';
import type { Command } from './commands';

/**
 * The single reactive store (AD-2). Wraps the pure default-state factory and
 * reducer with Svelte 5 runes so render and UI can subscribe. Consumers read
 * `store.state` (read-only) and mutate only by `store.dispatch(command)`; no
 * adapter writes fields directly.
 */
export interface Store {
  /** The current, reactive application state. Read-only for subscribers. */
  readonly state: AppState;
  /** Apply a command through the pure reducer. The only path for user commands. */
  dispatch(command: Command): void;
  /**
   * Advance the simulation clock by `dtSeconds` SI seconds (AD-2). This is the
   * sanctioned second channel for the injected clock, kept distinct from the
   * closed command union: `dispatch` is user commands, `tick` is the clock. Both
   * keep render and UI read-only.
   */
  tick(dtSeconds: number): void;
}

export function createStore(initial: AppState = createDefaultState()): Store {
  let state = $state<AppState>(initial);
  return {
    get state() {
      return state;
    },
    dispatch(command: Command) {
      state = reduce(state, command);
    },
    tick(dtSeconds: number) {
      state = advanceTime(state, dtSeconds);
    }
  };
}
